/**
 * Resolve Home portfolio vulnerability KPIs from competing API payloads.
 * Prefer the richest non-empty current-posture snapshot (never prefer all-zero
 * posture over a monitoring/security-health payload that has findings).
 */

function n(v) {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function severitySum(s) {
  return s.critical + s.high + s.medium + s.low + s.info
}

function fromPosture(posture) {
  if (!posture || typeof posture !== 'object') return null
  const dist = posture.severityDistribution || {}
  const critical = n(posture.critical ?? dist.critical)
  const high = n(posture.high ?? dist.high)
  const medium = n(posture.medium ?? dist.medium)
  const low = n(posture.low ?? dist.low)
  const info = n(posture.info ?? dist.info)
  const uniqueFindings = n(
    posture.currentUniqueFindings ?? posture.openFindings ?? posture.total,
  )
  const sev = { critical, high, medium, low, info }
  if (uniqueFindings <= 0 && severitySum(sev) <= 0) return null
  const risks = n(posture.openRisks)
  const controls = n(posture.failedControls)
  const vulns = uniqueFindings > 0 ? uniqueFindings : severitySum(sev)
  return {
    ...sev,
    uniqueFindings: vulns,
    assetsTotal: n(posture.assetsTotal),
    assetsWithVulnerabilities: n(posture.assetsWithVulnerabilities),
    informationSystemsCount: n(posture.informationSystemsTotal),
    problems: {
      vulnerabilities: vulns,
      risks,
      controls,
      total: vulns + risks + controls,
    },
    source: 'vulnerability-posture/current',
  }
}

function fromMonitoring(monitoring) {
  if (!monitoring || typeof monitoring !== 'object') return null
  const dist = monitoring.severityDistribution || {}
  const problems = monitoring.problems || {}
  const critical = n(monitoring.critical ?? dist.critical)
  const high = n(monitoring.high ?? dist.high)
  const medium = n(monitoring.medium ?? dist.medium)
  const low = n(monitoring.low ?? dist.low)
  const info = n(monitoring.info ?? dist.info)
  const uniqueFindings = n(
    monitoring.currentUniqueFindings ?? monitoring.openFindings ?? monitoring.total,
  )
  const sev = { critical, high, medium, low, info }
  if (uniqueFindings <= 0 && severitySum(sev) <= 0) return null
  const risks = n(problems.risks)
  const controls = n(problems.controls)
  const vulns = n(problems.vulnerabilities) || (uniqueFindings > 0 ? uniqueFindings : severitySum(sev))
  return {
    ...sev,
    uniqueFindings: uniqueFindings > 0 ? uniqueFindings : severitySum(sev),
    assetsTotal: n(monitoring.assetsCount),
    assetsWithVulnerabilities: n(monitoring.assetsWithVulnerabilities),
    informationSystemsCount: n(monitoring.informationSystemsCount),
    problems: {
      vulnerabilities: vulns,
      risks,
      controls,
      total: n(problems.total) || vulns + risks + controls,
    },
    source: 'home/assets-monitoring',
  }
}

function fromSecurityHealth(health) {
  if (!health || typeof health !== 'object') return null
  const sevIn = health.severity || health.severityDistribution || {}
  const critical = n(sevIn.critical)
  const high = n(sevIn.high)
  const medium = n(sevIn.medium)
  const low = n(sevIn.low)
  const info = n(sevIn.info)
  const uniqueFindings = n(health.currentUniqueFindings ?? health.openFindings ?? health.severityTotal)
  const sev = { critical, high, medium, low, info }
  if (uniqueFindings <= 0 && severitySum(sev) <= 0) return null
  const vulns = uniqueFindings > 0 ? uniqueFindings : severitySum(sev)
  return {
    ...sev,
    uniqueFindings: vulns,
    assetsTotal: n(health.assetsTotal),
    assetsWithVulnerabilities: n(health.assetsWithVulnerabilities),
    informationSystemsCount: n(health.informationSystemsTotal),
    problems: {
      vulnerabilities: vulns,
      risks: 0,
      controls: 0,
      total: vulns,
    },
    source: 'home/security-health',
  }
}

function score(k) {
  return k.uniqueFindings * 10 + severitySum(k)
}

/**
 * Pick the richest vulnerability KPI snapshot among Home API consumers.
 */
export function resolveHomeVulnerabilityKpis({
  posture = null,
  monitoring = null,
  securityHealth = null,
  assetsListCount = 0,
  risksFallback = 0,
  controlsFallback = 0,
} = {}) {
  const candidates = [fromPosture(posture), fromMonitoring(monitoring), fromSecurityHealth(securityHealth)].filter(
    Boolean,
  )
  candidates.sort((a, b) => score(b) - score(a))
  const best = candidates[0]
  if (!best) {
    return {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      info: 0,
      uniqueFindings: 0,
      assetsTotal: Math.max(n(monitoring?.assetsCount), n(posture?.assetsTotal), n(assetsListCount)),
      assetsWithVulnerabilities: 0,
      informationSystemsCount: Math.max(
        n(monitoring?.informationSystemsCount),
        n(posture?.informationSystemsTotal),
      ),
      problems: {
        total: n(risksFallback) + n(controlsFallback),
        vulnerabilities: 0,
        risks: n(risksFallback),
        controls: n(controlsFallback),
      },
      source: 'empty',
    }
  }

  const assetsTotal = Math.max(
    best.assetsTotal,
    n(monitoring?.assetsCount),
    n(posture?.assetsTotal),
    n(assetsListCount),
  )
  const informationSystemsCount = Math.max(
    best.informationSystemsCount,
    n(monitoring?.informationSystemsCount),
    n(posture?.informationSystemsTotal),
  )
  const risks = Math.max(best.problems.risks, n(risksFallback))
  const controls = Math.max(best.problems.controls, n(controlsFallback))
  return {
    ...best,
    assetsTotal,
    informationSystemsCount,
    problems: {
      vulnerabilities: best.uniqueFindings,
      risks,
      controls,
      total: best.uniqueFindings + risks + controls,
    },
  }
}
